// ─── Las cuatro horas de una visita, y cómo se dicen ──────────────────────────
//
// Una visita de descarga tiene cuatro instantes medidos por dos sistemas
// distintos, y en orden lógico son:
//
//   entrada a la geocerca (GPS) → entrada por la pluma → salida por la pluma →
//   salida de la geocerca (GPS)
//
// Este módulo decide QUÉ TEXTO se muestra para cada uno, en las tarjetas del
// historial y en el recorrido del modal. Es puro a propósito (ver
// CLAUDE.md, "Verificación"): el historial y el modal importan transitivamente
// `utils/api` → firebase, que no se puede levantar en un worktree sin
// `apps/v5/.env`, así que lo decidible vive acá y se prueba sin montar nada.
//
// Dos reglas que no son de estilo:
//
// 1. SEGUNDOS SIEMPRE. El GPS marca la entrada a la geocerca entre 0 y ~3
//    minutos alrededor del pase por la pluma. Con `timeStyle: "short"` (HH:mm,
//    lo que usa el resto de la pantalla) las dos horas salían idénticas en una
//    parte de las visitas y el usuario lo leyó como un dato duplicado por error.
//    No se cambió el formato del resto de la app: esta es una variante aparte.
//
// 2. UN DATO AUSENTE SE NOMBRA. "—" no distingue "todavía no pasó" de "no se
//    midió" de "no cargó", y "0"/"Invalid Date" son directamente falsos. Cada
//    hueco tiene su propia frase, y ninguna inventa una hora.

import { DEFAULT_YARD_TIME_ZONE } from "../../utils/yardDay";

/**
 * Millis desde las formas en que de verdad llega un timestamp de inspección.
 * Mismo contrato que `inspectionMillis` del store (que no se puede importar acá
 * sin arrastrar firebase): `/yardInspectionList` entrega epoch en milisegundos,
 * el tipo declara `string` (ISO), y un Timestamp de Firestore serializado
 * (`{_seconds}` / `{seconds}`) todavía aparece por rutas que no pasan por ese
 * endpoint. Lo irreconocible da 0, que acá se lee como "no hay dato".
 */
export function toYardMillis(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === "number") return Number.isFinite(ts) ? ts : 0;
  if (typeof ts === "string") {
    const d = new Date(ts).getTime();
    return Number.isNaN(d) ? 0 : d;
  }
  if (typeof ts === "object") {
    const o = ts as Record<string, unknown>;
    if (typeof o._seconds === "number") return o._seconds * 1000;
    if (typeof o.seconds === "number") return o.seconds * 1000;
  }
  return 0;
}

// ── Formato ───────────────────────────────────────────────────────────────────

// `undefined` como clave = zona del navegador, que es lo que ya hace
// `formatYardTs` (toLocaleString sin `timeZone`) cuando no se sabe de qué patio
// es la visita.
const formatters = new Map<string, Intl.DateTimeFormat>();

function clockFormatter(timeZone?: string): Intl.DateTimeFormat {
  const key = timeZone || "";
  const cached = formatters.get(key);
  if (cached) return cached;
  const options: Intl.DateTimeFormatOptions = {
    // h23 explícito por el mismo motivo que en utils/yardDay.ts: con h24 la
    // medianoche sale como "24:00:05" del día anterior.
    hourCycle: "h23",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat("es-MX", {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    // `truck_yards.timeZone` se persiste sin validar y ya hubo una zona no-IANA
    // en el catálogo cuatro meses ('America/El_Paso', ver utils/yardDay.ts).
    // Esto corre en render: un RangeError acá deja el historial en blanco. Una
    // hora corrida es mala; una pantalla en blanco es peor.
    formatter = new Intl.DateTimeFormat("es-MX", {
      ...options,
      timeZone: DEFAULT_YARD_TIME_ZONE,
    });
  }
  formatters.set(key, formatter);
  return formatter;
}

export interface YardClock {
  /** "dd/MM/yy" en la zona pedida. */
  date: string;
  /** "HH:mm:ss" en la zona pedida. */
  time: string;
}

/** Fecha y hora con segundos, o `null` si no hay instante que formatear. */
export function yardClock(ts: unknown, timeZone?: string): YardClock | null {
  const millis = toYardMillis(ts);
  if (!millis) return null;
  const parts = clockFormatter(timeZone).formatToParts(new Date(millis));
  const at = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  // Se arma desde las partes en vez de confiar en `format()`: el separador y el
  // orden de es-MX cambian entre versiones de ICU ("05/10/26, 10:25:03" vs
  // "5/10/26 10:25:03") y el test fijaría la versión de Node, no el contrato.
  return {
    date: `${at("day")}/${at("month")}/${at("year")}`,
    // `% 24`: red por si un motor ignora h23 y entrega "24".
    time: `${String(Number(at("hour")) % 24).padStart(2, "0")}:${at("minute")}:${at("second")}`,
  };
}

/**
 * "dd/MM/yy HH:mm" en la zona pedida. Para la cabecera de la tarjeta, que
 * resume la visita y no necesita segundos pero SÍ la misma zona que las horas
 * que tiene debajo.
 */
export function formatYardTsMinutes(ts: unknown, timeZone?: string): string {
  const clock = yardClock(ts, timeZone);
  return clock ? `${clock.date} ${clock.time.slice(0, 5)}` : "—";
}

// ── Las cuatro horas ──────────────────────────────────────────────────────────

export const MISSING_GATE_ENTRY = "Sin entrada registrada";
export const MISSING_GATE_EXIT = "Sin salida registrada";
// El controlador entregó el evento de salida (hay `access_exit_id`) pero su
// hora no es legible. Decir "Sin salida registrada" sería falso —la salida
// está ligada— y contradiría al pill de estado, que ya la da por cerrada.
export const UNREADABLE_GATE_EXIT = "Salida sin hora";
export const MISSING_GEOFENCE = "Sin cruce";

export type InspectionTimeKey =
  | "geofence_entry"
  | "gate_entry"
  | "gate_exit"
  | "geofence_exit";

export interface InspectionTimePoint {
  key: InspectionTimeKey;
  /** Qué sistema lo midió: la pluma (control de acceso) o la geocerca (GPS). */
  source: "gate" | "geofence";
  direction: "entry" | "exit";
  label: string;
  /** 0 cuando no hay dato. */
  millis: number;
  missing: boolean;
  /**
   * Para la tarjeta, que ya imprime la fecha de la visita en su encabezado:
   * sólo "HH:mm:ss", salvo que el instante caiga en otro día que la visita, en
   * cuyo caso lleva la fecha delante (una salida 00:10 del día siguiente leída
   * sin fecha parece anterior a la entrada de las 23:50).
   */
  text: string;
  /** Para lista y tabla, que no tienen encabezado de fecha: fecha + hora. */
  fullText: string;
  /**
   * Sólo cuando la hora mostrada es una COTA y no una lectura (ver
   * `boundByGate`): dice qué midió el GPS de verdad, para que el dato crudo no
   * se pierda detrás de la cota.
   */
  note?: string;
}

/** Los campos de `YardInspection` que este módulo lee, sin importar el store. */
export interface InspectionTimesInput {
  entry_timestamp?: unknown;
  exit_timestamp?: unknown;
  access_exit_id?: string | null;
  geofence_entry_at?: unknown;
  geofence_exit_at?: unknown;
}

const LABELS: Record<InspectionTimeKey, string> = {
  geofence_entry: "Geocerca · entrada",
  gate_entry: "Pluma · entrada",
  gate_exit: "Pluma · salida",
  geofence_exit: "Geocerca · salida",
};

/**
 * Las cuatro horas de la visita, SIEMPRE las cuatro y siempre en orden lógico
 * (geocerca entrada → pluma entrada → pluma salida → geocerca salida), con el
 * texto ya decidido para cada una.
 *
 * La salida por la pluma se rige por `access_exit_id`, no por
 * `exit_timestamp`: es el mismo criterio que usan el pill de permanencia y
 * `deriveInspectionStatus`, y si la tarjeta decidiera con otro campo podría
 * mostrar una hora de salida al lado de un pill que dice "En patio".
 */
export function inspectionTimePoints(
  inspection: InspectionTimesInput,
  timeZone?: string,
): InspectionTimePoint[] {
  const hasGateExit = !!inspection.access_exit_id;
  const raw: Array<{
    key: InspectionTimeKey;
    millis: number;
    missingText: string;
  }> = [
    {
      key: "geofence_entry",
      millis: toYardMillis(inspection.geofence_entry_at),
      missingText: MISSING_GEOFENCE,
    },
    {
      key: "gate_entry",
      millis: toYardMillis(inspection.entry_timestamp),
      missingText: MISSING_GATE_ENTRY,
    },
    {
      key: "gate_exit",
      millis: hasGateExit ? toYardMillis(inspection.exit_timestamp) : 0,
      missingText: hasGateExit ? UNREADABLE_GATE_EXIT : MISSING_GATE_EXIT,
    },
    {
      key: "geofence_exit",
      millis: toYardMillis(inspection.geofence_exit_at),
      missingText: MISSING_GEOFENCE,
    },
  ];

  // El día de la visita es el de la entrada por la pluma —lo mismo que ancla
  // `inspectionDateMillis` y el encabezado de la tarjeta—, con la salida de
  // respaldo. Sin ninguna de las dos no hay día de referencia y toda hora
  // presente lleva su fecha.
  const anchor =
    yardClock(raw[1].millis, timeZone) ?? yardClock(raw[2].millis, timeZone);

  const gateEntry = raw[1].millis;
  const gateExit = raw[2].millis;

  return raw.map(({ key, millis, missingText }) => {
    const clock = yardClock(millis, timeZone);
    const [source, direction] = key.split("_") as [
      "gate" | "geofence",
      "entry" | "exit",
    ];
    const base = { key, source, direction, label: LABELS[key], millis };
    if (!clock) {
      return { ...base, missing: true, text: missingText, fullText: missingText };
    }
    const fullText = `${clock.date} ${clock.time}`;
    const short = (c: YardClock) =>
      anchor && anchor.date === c.date ? c.time : `${c.date} ${c.time}`;

    const bound = boundByGate(key, millis, gateEntry, gateExit);
    if (bound) {
      const gate = yardClock(bound.gateMillis, timeZone);
      if (gate) {
        return {
          ...base,
          missing: false,
          text: `${bound.word} ${short(gate)}`,
          fullText: `${bound.word} ${gate.date} ${gate.time}`,
          // Con fecha si el reporte cae en otro día que la lectura de la pluma
          // (un cruce a medianoche): la nota existe para conservar el dato
          // crudo, y una hora sin su fecha no lo es.
          note: `El GPS la reportó ${bound.side} a las ${
            clock.date === gate.date ? clock.time : fullText
          }; el cruce real fue ${bound.word} la lectura de la pluma.`,
        };
      }
    }
    return {
      ...base,
      missing: false,
      text: short(clock),
      fullText,
    };
  });
}

/**
 * Cuándo la hora de geocerca que llega NO se puede mostrar como "la hora del
 * cruce", y qué se sabe en su lugar.
 *
 * Geocerca y pluma son dos sistemas independientes y el orden físico es uno
 * solo: la unidad cruza la geocerca, después llega a la pluma; sale por la
 * pluma, después cruza la geocerca. Pero lo que la plataforma de rastreo guarda
 * como `entered_at` no es el instante del cruce: es la hora de la PRIMERA
 * POSICIÓN de GPS que llegó con la unidad ya adentro, y el GPS reporta a
 * intervalos. La geocerca de Acuapue es un círculo de 260 m y la pluma queda a
 * segundos del borde, así que a veces la pluma lee el tag antes de que llegue
 * ese primer reporte: 44 de 247 inspecciones con ingreso, 43 de ellas por 1-60 s
 * y una por 167 s (medido el 5-oct-2026; el primer punto "adentro" cae a ~95 m
 * del borde en la mediana).
 *
 * Mostrar esa hora tal cual dice "entró a la geocerca DESPUÉS de entrar al
 * patio", que no puede ocurrir y fue reportado como un error. Lo único cierto es
 * la cota: el cruce fue antes de la lectura de la pluma. Eso se muestra, y la
 * hora del reporte queda en `note`. Para la salida es el espejo (un reporte de
 * "afuera" anterior a la pluma de salida), que en esos datos no pasó ninguna
 * vez de 187 pero es la misma imposibilidad.
 *
 * Sólo dentro de `GPS_REPORT_LAG_MAX_MS`. La cota es una explicación —"el GPS
 * avisó tarde"— y una explicación no puede cubrir cualquier distancia: un cruce
 * a horas de la pluma no es un reporte tardío, es un vínculo mal hecho, y
 * decirle "antes de <pluma>" lo escondería justo donde hay que verlo. Fuera de
 * la ventana la hora se muestra cruda, con su orden imposible a la vista.
 */
function boundByGate(
  key: InspectionTimeKey,
  millis: number,
  gateEntry: number,
  gateExit: number,
): { word: "antes de" | "después de"; side: "adentro" | "afuera"; gateMillis: number } | null {
  if (
    key === "geofence_entry" && gateEntry &&
    millis > gateEntry && millis - gateEntry <= GPS_REPORT_LAG_MAX_MS
  ) {
    return { word: "antes de", side: "adentro", gateMillis: gateEntry };
  }
  if (
    key === "geofence_exit" && gateExit &&
    millis < gateExit && gateExit - millis <= GPS_REPORT_LAG_MAX_MS
  ) {
    return { word: "después de", side: "afuera", gateMillis: gateExit };
  }
  return null;
}

// Hasta dónde un reporte de GPS del lado equivocado de la pluma se lee como
// "reporte tardío". Es el mismo margen con el que el backend vincula un cruce a
// un pase (`GATE_SKEW_MS`, 15 min, en geofenceCrossingResolver.js): más allá de
// eso el backend no lo habría vinculado, así que tampoco hay cota que explicar.
// El retraso más ancho medido es de 167 s.
export const GPS_REPORT_LAG_MAX_MS = 15 * 60 * 1000;

// ── El recorrido de la visita (grupo "Accesos y geocerca" del modal) ───────────

export const GEOFENCE_LOADING = "Buscando cruce…";
export const GEOFENCE_FAILED = "No se pudo cargar";

/** Lo que el modal sabe de la geocerca en el momento de pintar. */
export interface JourneyGeofenceState {
  /** El fetch de `/yardInspection/geofenceEvent` sigue en vuelo. */
  loading?: boolean;
  /** El fetch falló: no se sabe si hubo cruce. */
  failed?: boolean;
  /** La respuesta del fetch; `null` = respondió que no hay evento. */
  event?: {
    entered_at?: unknown;
    exited_at?: unknown;
    /** El cruce vinculado como ingreso / egreso, con su notificación. */
    ingreso?: { notification_id?: string | null } | null;
    egreso?: { notification_id?: string | null } | null;
  } | null;
}

export interface VisitJourneyStep {
  key: InspectionTimeKey;
  /** 1-4: la posición en el recorrido, que es lo que el usuario lee primero. */
  step: number;
  source: "gate" | "geofence";
  /** El sistema que lo midió, dicho corto para la etiqueta de cada paso. */
  sourceLabel: "GPS" | "Pluma";
  title: string;
  /** "dd/MM/yy HH:mm:ss", o la frase que nombra por qué no hay hora. */
  text: string;
  missing: boolean;
  /** Ver `InspectionTimePoint.note`: la hora es una cota, y esto dice de qué. */
  note?: string;
  /**
   * Sólo en los dos pasos de geocerca, y sólo cuando hay a dónde enlazar: el
   * id de la notificación que disparó ESE cruce. Ver `journeyNotificationId`.
   */
  notificationId?: string;
}

/**
 * El id de la notificación del cruce que muestra un paso, o `null` si ese paso
 * no lleva enlace.
 *
 * Tres condiciones, y cada una evita un enlace que miente:
 *
 * - Sólo geocerca. Los pasos de la pluma ya enlazan a su evento de acceso desde
 *   el detalle de abajo; un cruce de pluma no dispara notificación.
 * - Sólo con una hora en el paso. El id viene de `ingreso` / `egreso` y la hora
 *   de `entered_at` / `exited_at`: son campos distintos de la misma respuesta, y
 *   "Sin cruce — Ver notificación" ofrecería la notificación de un cruce que el
 *   mismo renglón dice que no hubo.
 * - Sólo con el fetch respondido. Con el fetch en vuelo o caído las horas salen
 *   del documento de la inspección, que no trae ids de notificación; un `event`
 *   que quedara de una respuesta anterior no describe lo que se está pintando.
 *
 * El id puede faltar sin que nada esté roto —cruces anteriores a que el backend
 * guardara el vínculo, o un backend todavía sin desplegar—, y entonces no hay
 * enlace: ni uno muerto ni uno a la lista general.
 */
function journeyNotificationId(
  key: InspectionTimeKey,
  missing: boolean,
  geofence: JourneyGeofenceState,
): string | null {
  if (missing || geofence.loading || geofence.failed) return null;
  const leg =
    key === "geofence_entry"
      ? geofence.event?.ingreso
      : key === "geofence_exit"
        ? geofence.event?.egreso
        : null;
  const id = leg?.notification_id;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

const JOURNEY_TITLES: Record<InspectionTimeKey, string> = {
  geofence_entry: "Entrada a la geocerca",
  gate_entry: "Entrada al patio",
  gate_exit: "Salida del patio",
  geofence_exit: "Salida de la geocerca",
};

/**
 * Los cuatro momentos de la visita en el orden en que ocurren, para el
 * recorrido del modal. Geocerca (GPS del rastreador) y pluma (relay Hikvision)
 * son dos sistemas que no se conocen entre sí; lo que el usuario quiere leer es
 * el CRUCE de los dos: entró a la geocerca → entró al patio → salió del patio →
 * salió de la geocerca. Por eso son una sola lista y no dos secciones, que es
 * como quedaron en el #723 y lo que el usuario devolvió.
 *
 * De dónde salen las dos horas de geocerca:
 *
 * - Con el fetch respondido manda la RESPUESTA, aunque diga que no hay evento:
 *   es lo que el backend acaba de re-resolver y es de donde sale el detalle de
 *   abajo (coordenadas, permanencia), así que recorrido y detalle no pueden
 *   contradecirse.
 * - Con el fetch en vuelo o caído mandan los cruces que el documento ya trae
 *   vinculados (`geofence_entry_at` / `geofence_exit_at`): son las mismas dos
 *   horas que la tarjeta del historial muestra justo detrás del modal.
 * - Y si en ese caso el documento tampoco las trae, NO se dice "Sin cruce":
 *   esa frase afirma que no hubo cruce, y con el fetch sin responder eso no se
 *   sabe. Se dice que se está buscando o que no se pudo cargar.
 *
 * Siempre fecha + hora (`fullText`): el modal no tiene un encabezado con la
 * fecha de la visita como la tarjeta, y una salida 00:10 sin fecha se lee
 * anterior a la entrada de las 23:50.
 */
export function visitJourney(
  inspection: InspectionTimesInput,
  geofence: JourneyGeofenceState,
  timeZone?: string,
): VisitJourneyStep[] {
  const unresolved = !!geofence.loading || !!geofence.failed;
  const points = inspectionTimePoints(
    unresolved
      ? inspection
      : {
          ...inspection,
          geofence_entry_at: geofence.event?.entered_at,
          geofence_exit_at: geofence.event?.exited_at,
        },
    timeZone,
  );
  return points.map((point, index) => {
    let text = point.fullText;
    if (point.missing && point.source === "geofence" && unresolved) {
      text = geofence.loading ? GEOFENCE_LOADING : GEOFENCE_FAILED;
    }
    const notificationId = journeyNotificationId(
      point.key,
      point.missing,
      geofence,
    );
    return {
      key: point.key,
      step: index + 1,
      source: point.source,
      sourceLabel: point.source === "geofence" ? "GPS" : "Pluma",
      title: JOURNEY_TITLES[point.key],
      text,
      missing: point.missing,
      ...(point.note ? { note: point.note } : {}),
      ...(notificationId ? { notificationId } : {}),
    };
  });
}

// ── El patio en el título del modal ───────────────────────────────────────────

/**
 * El nombre del patio para el título del modal, o `null` si no hay nombre que
 * poner. `null` y no "Sin patio asignado": en una sección propia esa frase era
 * un dato; pegada al título se lee como el nombre de un patio.
 */
export function modalYardName(yardName: unknown): string | null {
  const name = typeof yardName === "string" ? yardName.trim() : "";
  return name || null;
}
